import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Gift, FileText, RefreshCw, ChevronRight } from "lucide-react";
import { useAuth } from "@/App";
import { colors } from "./constants";
import HowItWorksModal from "./components/HowItWorksModal";
import LivePredictionsInfoCard from "./components/LivePredictionsInfoCard";
import AboutSection from "./components/FAQSection";
import MatchCard from "./components/MatchCard";
import NextMatchCard from "./components/NextMatchCard";
import TomorrowMatchCard from "./components/TomorrowMatchCard";
import ResultCard from "./components/ResultCard";
import RewardsContent from "./components/RewardsContent";

import { BACKEND_URL } from "@/lib/api";
import { PREDICTION_WINDOW_MS, MATCH_POLL_INTERVAL_MS } from "@/constants";

const MatchList = ({ user }) => {
  const [matches, setMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPredictions, setUserPredictions] = useState(() => {
    // Seed from localStorage immediately so cards show prediction state before API responds
    try {
      const userId = user?.user_id || user?.id;
      if (!userId) return {};
      const result = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(`hpl_pred_${userId}_`)) {
          const matchId = key.replace(`hpl_pred_${userId}_`, "");
          const val = parseInt(localStorage.getItem(key), 10);
          if (!isNaN(val)) result[matchId] = val;
        }
      }
      return result;
    } catch (_) {
      return {};
    }
  });
  const [activeTab, setActiveTab] = useState("matches");
  const [showAllToday, setShowAllToday] = useState(false);
  const [showAllTomorrow, setShowAllTomorrow] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();

  const [refreshing, setRefreshing] = useState(false);
  const [userPredictionsByMatch, setUserPredictionsByMatch] = useState({});
  const [performanceByMatch, setPerformanceByMatch] = useState({});

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([fetchMatches(), fetchLiveMatches(), fetchUserPredictions()]);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchMatches();
    fetchLiveMatches();
    fetchUserPredictions();
    const interval = setInterval(() => {
      fetchMatches();
      fetchLiveMatches();
    }, MATCH_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  const fetchUserPredictions = async () => {
    try {
      const userId = user?.user_id || user?.id;
      if (!userId) return;
      const response = auth?.authFetch
        ? await auth.authFetch(`${BACKEND_URL}/api/rewards/performance/${userId}`)
        : await fetch(`${BACKEND_URL}/api/rewards/performance/${userId}`);
      if (response.ok) {
        const data = await response.json();
        const byMatch = {};
        const perfByMatch = {};
        for (const p of data.performance_history || []) {
          byMatch[p.match_id] = p.predicted_runs;
          perfByMatch[p.match_id] = p;
        }
        setUserPredictionsByMatch(byMatch);
        setPerformanceByMatch(perfByMatch);

        // Clean up localStorage for server-confirmed predictions (no longer need local cache)
        try {
          const userId = user?.user_id || user?.id;
          if (userId) {
            for (const matchId of Object.keys(byMatch)) {
              localStorage.removeItem(`hpl_pred_${userId}_${matchId}`);
            }
          }
        } catch (_) {}
      }
    } catch (_error) {
      // silently ignore
    }
  };

  const fetchMatches = async () => {
    try {
      const response = auth?.authFetch
        ? await auth.authFetch(`${BACKEND_URL}/api/matches`)
        : await fetch(`${BACKEND_URL}/api/matches`);
      const data = await response.json();
      setMatches(data);
    } catch (_error) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveMatches = async () => {
    try {
      // Use user_id if authenticated, otherwise fall back to user.id
      const userId = user?.user_id || user?.id;
      const response = auth?.authFetch
        ? await auth.authFetch(`${BACKEND_URL}/api/live-cricket?user_id=${userId}`)
        : await fetch(`${BACKEND_URL}/api/live-cricket?user_id=${userId}`);
      const data = await response.json();
      const normalizedMatches = (data.matches || []).map((match) => {
        const team1Score = match.scores?.find((s) =>
          s.inning?.toLowerCase().includes(match.teams?.[0]?.toLowerCase()),
        );
        const team2Score = match.scores?.find((s) =>
          s.inning?.toLowerCase().includes(match.teams?.[1]?.toLowerCase()),
        );
        return {
          id: match.db_match_id || match.id,
          match_number: match.match_number,
          team_a: match.teams?.[0] || "Team A",
          team_b: match.teams?.[1] || "Team B",
          team_a_score: match.team_a_score ?? team1Score?.runs ?? null,
          team_a_wickets: match.team_a_wickets ?? team1Score?.wickets ?? null,
          team_a_overs: match.team_a_overs ?? team1Score?.overs ?? null,
          team_b_score: match.team_b_score ?? team2Score?.runs ?? null,
          team_b_wickets: match.team_b_wickets ?? team2Score?.wickets ?? null,
          team_b_overs: match.team_b_overs ?? team2Score?.overs ?? null,
          current_over: match.current_over ?? null,
          venue: match.venue || "",
          match_date: match.date || "",
          prediction_closes_at: match.prediction_closes_at || match.date || "",
          status: match.is_live ? "live" : match.is_completed ? "completed" : "upcoming",
          is_live: match.is_live,
          is_completed: match.is_completed,
          starts_soon: match.starts_soon,
          total_runs: match.total_runs || 0,
          user_prediction: match.user_prediction,
          prediction_count: match.prediction_count || 0,
        };
      });
      setLiveMatches(normalizedMatches);
    } catch (_error) {
      // silently ignore
    }
  };

  const handlePredictionSubmit = (matchId, prediction) => {
    setUserPredictions((prev) => ({ ...prev, [matchId]: prediction }));
  };

  const canPredict = (match) => {
    if (match.is_live || match.is_completed) return false;
    const closesAt = match.prediction_closes_at || match.match_date;
    if (closesAt) {
      const closes = new Date(closesAt);
      if (isNaN(closes.getTime())) return false;
      const now = new Date();
      const opens = new Date(closes.getTime() - PREDICTION_WINDOW_MS);
      return now >= opens && now < closes;
    }
    return false;
  };

  const todayMatches = [
    ...liveMatches.filter((m) => m.is_live || m.starts_soon),
    ...matches.filter((m) => {
      if (m.status === "live") return true;
      if (m.status !== "upcoming") return false;
      // Only include upcoming matches that start today
      const matchDate = new Date(m.match_date || m.prediction_closes_at);
      const now = new Date();
      return matchDate.toDateString() === now.toDateString();
    }),
  ].filter((match, index, self) => index === self.findIndex((m) => m.id === match.id));

  const allLiveMatches = liveMatches.filter((m) => m.is_live);

  // Compute the next upcoming match (used when no matches today)
  const nextUpcomingMatch =
    todayMatches.length === 0
      ? [...matches, ...liveMatches]
          .filter((m) => m.status === "upcoming" && !m.is_live && !m.is_completed)
          .filter((m) => {
            const d = new Date(m.match_date || m.date || m.prediction_closes_at);
            return !isNaN(d.getTime()) && d > new Date();
          })
          .sort((a, b) => new Date(a.match_date || a.date) - new Date(b.match_date || b.date))[0] ||
        null
      : null;

  const upcomingMatches = liveMatches
    .filter((m) => !m.is_live && !m.is_completed)
    .filter((m) => !nextUpcomingMatch || m.id !== nextUpcomingMatch.id)
    .filter((m) => !todayMatches.some((t) => t.id === m.id));
  const completedMatches = [
    ...liveMatches
      .filter((m) => m.is_completed)
      .map((m) => ({
        ...m,
        user_prediction: userPredictions[m.id] || userPredictionsByMatch[m.id] || m.user_prediction,
        credits_earned: performanceByMatch[m.id]?.credits_earned ?? null,
        backend_result_type: performanceByMatch[m.id]?.result_type ?? null,
      })),
    ...matches
      .filter((m) => m.status === "completed")
      .map((m) => ({
        ...m,
        user_prediction: userPredictions[m.id] || userPredictionsByMatch[m.id] || m.user_prediction,
        credits_earned: performanceByMatch[m.id]?.credits_earned ?? null,
        backend_result_type: performanceByMatch[m.id]?.result_type ?? null,
      })),
  ]
    .filter((match, index, self) => index === self.findIndex((m) => m.id === match.id))
    .sort((a, b) => {
      const dateA = new Date(a.match_date || a.date || 0);
      const dateB = new Date(b.match_date || b.date || 0);
      return dateB - dateA;
    });

  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [howItWorksScrollTo, setHowItWorksScrollTo] = useState(null);

  const tabs = [
    { key: "matches", label: "Matches" },
    { key: "results", label: "Results" },
    { key: "rewards", label: "Rewards", icon: Gift },
  ];

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: colors.bg }}
      >
        <div className="w-8 h-8 border-2 border-[#01ff9a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: colors.bg }}>
      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={showHowItWorks}
        onClose={() => {
          setShowHowItWorks(false);
          setHowItWorksScrollTo(null);
        }}
        scrollToSection={howItWorksScrollTo}
      />

      {/* Main Content - 20px horizontal padding */}
      <div className="relative z-10 px-5 py-6 max-w-md mx-auto">
        {/* Banner */}
        <div className="mb-4">
          <img
            src="/banner-home.png"
            alt="Hudle Premier League - Win ₹25 Lakhs"
            className="w-full h-auto rounded-2xl"
          />
        </div>

        {/* Live Predictions info card (auto-hides once engaged) */}
        <LivePredictionsInfoCard
          onLearnMore={() => {
            setHowItWorksScrollTo("live-predictions");
            setShowHowItWorks(true);
          }}
        />

        {/* Tab Bar */}
        <div
          className="flex gap-2 p-1.5 rounded-2xl"
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.cardBorder}`,
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-semibold transition-all ${
                activeTab === tab.key ? "text-[#0f0f0f]" : "text-white/50 hover:text-white/70"
              }`}
              style={{
                background:
                  activeTab === tab.key
                    ? "linear-gradient(90deg, #01ff9a, #acffe5)"
                    : "transparent",
              }}
            >
              {tab.icon && <tab.icon className="w-3 h-3 inline mr-1" />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content - 24px top spacing after tabs */}
        <div className="mt-6">
          {activeTab === "matches" && (
            <div>
              {/* Today Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wide">
                      {todayMatches.length > 0 ? "Today" : "Upcoming"}
                    </h2>
                    <button
                      onClick={refreshAll}
                      disabled={refreshing}
                      className="p-1.5 rounded-full transition-all hover:bg-white/10"
                      title="Refresh scores"
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 text-white/40 ${refreshing ? "animate-spin" : ""}`}
                      />
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      const faqSection = document.getElementById("faq-section");
                      if (faqSection) {
                        faqSection.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="text-sm font-medium hover:underline transition-all"
                    style={{ color: "rgba(255,255,255,0.7)" }}
                  >
                    How it works
                  </button>
                </div>

                {todayMatches.length > 0 ? (
                  <div className="flex flex-col" style={{ gap: "20px" }}>
                    {todayMatches.map((match, index) => (
                      <MatchCard
                        key={match.id}
                        match={match}
                        userPrediction={
                          userPredictions[match.id] ||
                          userPredictionsByMatch[match.id] ||
                          match.user_prediction
                        }
                        canPredict={canPredict(match)}
                        user={user}
                        onPredictionSubmit={handlePredictionSubmit}
                        isLiveMatch={match.is_live}
                        showTimeline={true}
                        matchNumber={match.match_number}
                      />
                    ))}
                  </div>
                ) : nextUpcomingMatch ? (
                  <NextMatchCard match={nextUpcomingMatch} />
                ) : (
                  <div className="text-center py-8">
                    <p style={{ color: colors.textMuted }}>No upcoming matches</p>
                  </div>
                )}
              </div>

              {/* Upcoming Section */}
              {upcomingMatches.length > 0 && (
                <div>
                  {todayMatches.length > 0 && (
                    <h2 className="text-sm font-semibold text-white/70 mb-4 uppercase tracking-wide">
                      Upcoming
                    </h2>
                  )}
                  <div className="flex flex-col" style={{ gap: "20px" }}>
                    {(showAllTomorrow ? upcomingMatches : upcomingMatches.slice(0, 2)).map(
                      (match, index) => (
                        <TomorrowMatchCard
                          key={match.id}
                          match={match}
                          matchNumber={match.match_number}
                        />
                      ),
                    )}
                  </div>
                  {upcomingMatches.length > 2 && !showAllTomorrow && (
                    <button
                      onClick={() => setShowAllTomorrow(true)}
                      className="w-full mt-5 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all hover:bg-white/10"
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        color: colors.accent,
                      }}
                    >
                      View All ({upcomingMatches.length})
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}

              {/* About Section at bottom */}
              <AboutSection />
            </div>
          )}

          {activeTab === "results" && (
            <div className="flex flex-col" style={{ gap: "20px" }}>
              {completedMatches.length > 0 ? (
                completedMatches.map((match, index) => (
                  <ResultCard key={match.id} match={match} matchNumber={match.match_number} />
                ))
              ) : (
                <div className="text-center py-16">
                  <p style={{ color: colors.textMuted }}>No completed matches yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === "rewards" && (
            <RewardsContent user={user} onNavigateToToday={() => setActiveTab("matches")} />
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 pb-8 border-t border-white/10 text-center">
          <a
            href="/terms"
            className="text-xs text-white/30 hover:text-white/50 inline-flex items-center gap-1"
          >
            <FileText className="w-3 h-3" />
            Terms & Conditions
          </a>
        </div>
      </div>
    </div>
  );
};

export default MatchList;
